/**
 * Modern API Configuration
 * 
 * This file contains all configuration settings for the modern API architecture.
 * It includes feature flags, API endpoints, and migration settings.
 */

// API Base URLs
export const API_CONFIG = {
    MODERN_BASE_URL: process.env.REACT_APP_API_BASE_URL || '/api/v1',
    LEGACY_BASE_URL: process.env.REACT_APP_LEGACY_API_BASE_URL || '/legacy',
    TIMEOUT: 30000,
    RETRY_ATTEMPTS: 3,
    RETRY_DELAY: 1000
};

// Feature Flags for Modern Services
export const FEATURE_FLAGS = {
    USE_MODERN_USER_SERVICE: process.env.REACT_APP_USE_MODERN_USER_SERVICE === 'true',
    USE_MODERN_RBAC_SERVICE: process.env.REACT_APP_USE_MODERN_RBAC_SERVICE === 'true',
    USE_MODERN_PATIENT_SERVICE: process.env.REACT_APP_USE_MODERN_PATIENT_SERVICE === 'true',
    USE_MODERN_FORMS_SERVICE: process.env.REACT_APP_USE_MODERN_FORMS_SERVICE === 'true',
    USE_MODERN_MASTER_DATA_SERVICE: process.env.REACT_APP_USE_MODERN_MASTER_DATA_SERVICE === 'true',
    ENABLE_MIGRATION_LOGGING: process.env.REACT_APP_ENABLE_MIGRATION_LOGGING === 'true',
    ENABLE_FALLBACK: process.env.REACT_APP_ENABLE_FALLBACK === 'true',
    ENABLE_PERFORMANCE_MONITORING: process.env.REACT_APP_ENABLE_PERFORMANCE_MONITORING === 'true'
};

// Migration Settings
export const MIGRATION_CONFIG = {
    MODE: process.env.REACT_APP_MIGRATION_MODE || 'gradual', // 'gradual' | 'immediate' | 'manual'
    BATCH_SIZE: 10,
    MIGRATION_DELAY: 1000,
    ROLLBACK_ENABLED: true,
    VALIDATION_ENABLED: true
};

// Error Handling Configuration
export const ERROR_CONFIG = {
    ENABLE_ERROR_BOUNDARY: process.env.REACT_APP_ENABLE_ERROR_BOUNDARY === 'true',
    SHOW_ERROR_DETAILS: process.env.REACT_APP_SHOW_ERROR_DETAILS === 'true',
    LOG_ERRORS: true,
    TOAST_ERRORS: true,
    RETRY_ON_ERROR: true,
    MAX_RETRIES: 3
};

// Performance Monitoring Configuration
export const PERFORMANCE_CONFIG = {
    ENABLE_MONITORING: process.env.REACT_APP_ENABLE_PERFORMANCE_MONITORING === 'true',
    LOG_API_CALLS: process.env.REACT_APP_LOG_API_CALLS === 'true',
    SLOW_REQUEST_THRESHOLD: 5000, // milliseconds
    TRACK_MEMORY_USAGE: true,
    TRACK_RENDER_TIME: true
};

// Toast Notification Configuration
export const TOAST_CONFIG = {
    DURATION: parseInt(process.env.REACT_APP_TOAST_DURATION) || 5000,
    POSITION: process.env.REACT_APP_TOAST_POSITION || 'top-right',
    MAX_TOASTS: 5,
    SHOW_CLOSE_BUTTON: true,
    SHOW_PROGRESS_BAR: true
};

// Security Configuration
export const SECURITY_CONFIG = {
    TOKEN_STORAGE_KEY: process.env.REACT_APP_TOKEN_STORAGE_KEY || 'authToken',
    SESSION_TIMEOUT: parseInt(process.env.REACT_APP_SESSION_TIMEOUT) || 3600000, // 1 hour
    REFRESH_TOKEN_THRESHOLD: 300000, // 5 minutes before expiry
    SECURE_STORAGE: true,
    ENCRYPT_STORAGE: false
};

// Development Configuration
export const DEV_CONFIG = {
    ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT || 'development',
    DEBUG_MODE: process.env.REACT_APP_DEBUG_MODE === 'true',
    MOCK_API: process.env.REACT_APP_MOCK_API === 'true',
    VERBOSE_LOGGING: process.env.NODE_ENV === 'development',
    SHOW_REDUX_DEVTOOLS: process.env.NODE_ENV === 'development'
};

// Feature Toggles
export const FEATURE_TOGGLES = {
    ENABLE_DARK_MODE: process.env.REACT_APP_ENABLE_DARK_MODE === 'true',
    ENABLE_OFFLINE_MODE: process.env.REACT_APP_ENABLE_OFFLINE_MODE === 'true',
    ENABLE_PWA: process.env.REACT_APP_ENABLE_PWA === 'true',
    ENABLE_ANALYTICS: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
    ENABLE_NOTIFICATIONS: true,
    ENABLE_REAL_TIME_UPDATES: false
};

// API Endpoints Configuration
export const ENDPOINTS = {
    // Authentication
    AUTH: {
        LOGIN: '/auth/login',
        LOGOUT: '/auth/logout',
        REFRESH: '/auth/refresh',
        FORGOT_PASSWORD: '/auth/forgot-password',
        RESET_PASSWORD: '/auth/reset-password',
        CHANGE_PASSWORD: '/auth/change-password',
        VERIFY_OTP: '/auth/verify-otp'
    },
    
    // User Management
    USERS: {
        BASE: '/users',
        PROFILE: '/users/profile',
        SEARCH: '/users/search',
        BULK: '/users/bulk',
        ROLES: '/users/{id}/roles',
        PERMISSIONS: '/users/{id}/permissions'
    },
    
    // RBAC
    RBAC: {
        ROLES: '/rbac/roles',
        PERMISSIONS: '/rbac/permissions',
        ROLE_PERMISSIONS: '/rbac/roles/{id}/permissions'
    },
    
    // Patient Management
    PATIENTS: {
        BASE: '/patients',
        SEARCH: '/patients/search',
        MEDICAL_HISTORY: '/patients/{id}/medical-history',
        APPOINTMENTS: '/patients/{id}/appointments',
        MEDICATIONS: '/patients/{id}/medications',
        DOCUMENTS: '/patients/{id}/documents',
        STATISTICS: '/patients/statistics'
    },
    
    // Dynamic Forms
    FORMS: {
        BASE: '/dynamic-forms/forms',
        SUBMIT: '/dynamic-forms/forms/{id}/submit',
        SUBMISSIONS: '/dynamic-forms/forms/{id}/submissions',
        FIELDS: '/dynamic-forms/forms/{id}/fields',
        DEPARTMENTS: '/dynamic-forms/departments',
        CATEGORIES: '/dynamic-forms/categories',
        FIELD_TYPES: '/dynamic-forms/field-types',
        FORM_TYPES: '/dynamic-forms/form-types',
        TEMPLATES: '/dynamic-forms/templates'
    },
    
    // Master Data
    MASTER_DATA: {
        BASE: '/master-data',
        SEARCH: '/master-data/search',
        TYPES: '/master-data/types',
        CATEGORIES: '/master-data/categories',
        IMPORT: '/master-data/import',
        EXPORT: '/master-data/export',
        VALIDATE: '/master-data/validate'
    },
    
    // Health Check
    HEALTH: '/health',
    
    // Legacy Compatibility
    LEGACY: {
        DASHBOARD_STATS: '/legacy/dashboard-stats',
        AUTH_SMS: '/legacy/auth/sms-verification'
    },

    // Dashboard
    GET_DASHBOARD_STATS: '/dashboard/stats',
    GET_LEGACY_DASHBOARD_STATS: '/dashboard/legacy-stats',

    // Audit
    GET_AUDIT_ENTRIES: '/audit/entries',
};

// Validation Rules
export const VALIDATION_RULES = {
    PASSWORD: {
        MIN_LENGTH: 8,
        REQUIRE_UPPERCASE: true,
        REQUIRE_LOWERCASE: true,
        REQUIRE_NUMBER: true,
        REQUIRE_SPECIAL_CHAR: true
    },
    EMAIL: {
        PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    },
    PHONE: {
        PATTERN: /^[\+]?[1-9][\d]{0,15}$/
    },
    MRN: {
        PATTERN: /^[A-Z0-9]{6,12}$/
    }
};

// Cache Configuration
export const CACHE_CONFIG = {
    ENABLE_CACHING: true,
    DEFAULT_TTL: 300000, // 5 minutes
    MAX_CACHE_SIZE: 100, // number of items
    CACHE_STRATEGIES: {
        USERS: 'memory',
        FORMS: 'sessionStorage',
        MASTER_DATA: 'localStorage'
    }
};

// Pagination Configuration
export const PAGINATION_CONFIG = {
    DEFAULT_PAGE_SIZE: 20,
    MAX_PAGE_SIZE: 100,
    SHOW_SIZE_CHANGER: true,
    SHOW_QUICK_JUMPER: true,
    SHOW_TOTAL: true,
    PAGE_SIZE_OPTIONS: [10, 20, 50, 100]
};

// Export all configurations
export default {
    API_CONFIG,
    FEATURE_FLAGS,
    MIGRATION_CONFIG,
    ERROR_CONFIG,
    PERFORMANCE_CONFIG,
    TOAST_CONFIG,
    SECURITY_CONFIG,
    DEV_CONFIG,
    FEATURE_TOGGLES,
    ENDPOINTS,
    VALIDATION_RULES,
    CACHE_CONFIG,
    PAGINATION_CONFIG
};

// Helper function to get configuration value
export const getConfig = (path, defaultValue = null) => {
    const keys = path.split('.');
    let value = { API_CONFIG, FEATURE_FLAGS, MIGRATION_CONFIG, ERROR_CONFIG, PERFORMANCE_CONFIG, TOAST_CONFIG, SECURITY_CONFIG, DEV_CONFIG, FEATURE_TOGGLES, ENDPOINTS, VALIDATION_RULES, CACHE_CONFIG, PAGINATION_CONFIG };
    
    for (const key of keys) {
        value = value?.[key];
        if (value === undefined) {
            return defaultValue;
        }
    }
    
    return value;
};

// Helper function to check if feature is enabled
export const isFeatureEnabled = (featureName) => {
    return FEATURE_FLAGS[featureName] || FEATURE_TOGGLES[featureName] || false;
};

// Helper function to get API endpoint
export const getEndpoint = (path, params = {}) => {
    let endpoint = getConfig(`ENDPOINTS.${path}`);
    
    if (endpoint && params) {
        Object.keys(params).forEach(key => {
            endpoint = endpoint.replace(`{${key}}`, params[key]);
        });
    }
    
    return endpoint;
}; 